import { AppHeader, AppSidebar, type AppSidebarItem, PlaneSwitcherMenu, UserMenu } from '@today/ui-components'
import { PlaneShellFrame } from '@today/ui-domain'
import type { ReactElement } from 'react'
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { useViewerPermissions } from '@/auth/useViewerPermissions'
import { useAuth } from '@/hooks/useAuth'
import { tenantAdminNavGroups, tenantAdminNavPredicates } from '../tenant-admin-nav'
import { PlaneId, planeBasePath, usePlaneTargets } from '../usePlaneTargets'

/** Account-menu actions; the ids are reported by `UserMenu.onAction`. */
const ACCOUNT_ENTRIES = [
	{ id: 'settings', label: 'Settings' },
	{ id: 'logout', label: 'Logout' },
]

/**
 * Renders one tenant-admin nav item. A built item is a plain `NavLink`; a
 * not-yet-built item (`item.badge` set) appends a small `ds-*` "WIP" chip after the
 * label so the destination's placeholder status is visible, and composes the
 * accessible name as label + badge. The badge render lives here (not in the shared
 * `AppSidebar`) so the shared component stays unchanged (TO-635 D4).
 */
function renderTenantNavLink(
	item: AppSidebarItem,
	props: {
		target: string
		className: string
		children: string
		'aria-current': 'page' | undefined
	},
): ReactElement {
	return (
		<NavLink to={props.target} aria-current={props['aria-current']} className={props.className}>
			{props.children}
			{item.badge ? (
				<span className="ml-ds-2 rounded-ds-sm bg-ds-surface-muted px-ds-1 text-ds-xs font-medium uppercase text-ds-text-subtle">
					{item.badge}
				</span>
			) : null}
		</NavLink>
	)
}

/** The tenant sidebar: the nav config (bound to the viewer's own slug) + predicates bound to the viewer's atoms. */
function TenantAdminSidebar({
	slug,
	permissions,
	currentPath,
}: {
	slug: string
	permissions: readonly string[]
	currentPath: string
}) {
	return (
		<AppSidebar
			groups={tenantAdminNavGroups(slug)}
			predicates={tenantAdminNavPredicates}
			permissions={permissions}
			currentPath={currentPath}
			label="Tenant admin navigation"
			renderLink={renderTenantNavLink}
		/>
	)
}

/**
 * Tenant Admin plane shell (AD-4 / W1-3).
 *
 * A thin data+policy binder over the shared TO-633 substrate — the tenant-admin
 * twin of `PlatformShell` (TO-634). It feeds the tenant-admin nav config (scoped to
 * the viewer's OWN tenant slug) + the viewer's permission atoms to `AppSidebar`,
 * the own-tenant slug as the plane title + a `PlaneSwitcherMenu` (current =
 * tenant-admin) + a `UserMenu` to `AppHeader`, and the routed `<Outlet/>` to
 * `PlaneShellFrame`. It reimplements no presentation and renders no footer (a
 * platform-console concern). Mounted at `/t/:slug/admin`, replacing the shared
 * `Layout` for the tenant-admin plane only (`frontend-architecture.md` §4).
 */
export function TenantAdminShell(): ReactElement {
	const { user, logout } = useAuth()
	const { permissions, tenantSlug } = useViewerPermissions()
	const { targets } = usePlaneTargets()
	const location = useLocation()
	const navigate = useNavigate()

	// The viewer's own tenant slug (from /auth/me), never the URL :slug — the nav
	// (and the switcher) must stay on the viewer's own tenant (AD-8 / ADR-0032).
	const slug = tenantSlug ?? ''

	function handleAccountAction(id: string): void {
		if (id === 'logout') {
			logout()
			return
		}
		if (id === 'settings') void navigate('/settings')
	}

	function handlePlaneSwitch(planeId: string): void {
		void navigate(planeBasePath(planeId as PlaneId, tenantSlug))
	}

	// Hide the switcher when the tenant-admin plane is the only plane the viewer can
	// reach: a one-entry switcher offers no navigation (REQ-005).
	const planeSwitcher =
		targets.length > 1 ? (
			<PlaneSwitcherMenu
				targets={targets}
				permissions={permissions}
				current={PlaneId.TenantAdmin}
				onSwitch={handlePlaneSwitch}
				label="Tenant Admin"
			/>
		) : undefined

	const accountLabel = user?.displayName || user?.email || 'Account'
	const userMenu = <UserMenu label={accountLabel} entries={ACCOUNT_ENTRIES} onAction={handleAccountAction} />

	return (
		<PlaneShellFrame
			sidebar={<TenantAdminSidebar slug={slug} permissions={permissions} currentPath={location.pathname} />}
			header={<AppHeader title={slug} planeSwitcher={planeSwitcher} userMenu={userMenu} />}
		>
			<Outlet />
		</PlaneShellFrame>
	)
}
